// Moteur de guidage : machine à états de la tournée.
//
// Aucun accès direct au DOM : tout l'affichage passe par la « vue » injectée via
// initGuidance(). C'est ce qui permet de tester la machine à états — la partie où un bug
// coûte cher à un chauffeur — sans simuler un navigateur.

import { haversineMeters, distanceToRoute, bearingDegrees, formatDistance, spokenDistance } from './geo.js';
import { describeManeuver } from './maneuvers.js';
import { fetchRouteChunk, chunkWaypoints, OSRM_MAX_WAYPOINTS } from './routing.js';
import { announce, announceOnce, resetAnnouncements } from './voice.js';

// Vue par défaut inerte : la machine à états tourne même sans interface (tests).
let view = {
    turn() {}, stop() {}, hideStop() {}, stopAhead() {}, finished() {}, error() {},
    status() {}, route() {}
};
let log = () => {};
let getSpeedKmh = () => 0;

function initGuidance(deps) {
    if (deps.view) view = deps.view;
    if (deps.log) log = deps.log;
    if (deps.getSpeedKmh) getSpeedKmh = deps.getSpeedKmh;
}

// --- État de la tournée ---
// La liste des arrêts à desservir. buildNavigationRoute en est le seul propriétaire : il ne
// l'installe qu'en cas de succès, pour qu'un recalcul raté hors ligne ne la détruise pas.
let plannedWaypoints = [];
// Cap d'arrivée à chaque arrêt, relevé sur le tracé d'ORIGINE et jamais recalculé ensuite.
// Tableau parallèle à plannedWaypoints, donc décalé d'un cran : l'index 0 est le départ.
let plannedBearings = [];
let navManeuvers = [];
let navStopPoints = [];   // les arrêts seuls, extraits de navManeuvers
let navIndex = 0;         // manœuvre actuellement visée
let navStopIndex = 0;     // prochain arrêt à desservir
let navRouteCoords = [];  // tracé complet, pour la détection hors trajet
let navNearestIdx = 0;    // curseur sur navRouteCoords (voir distanceToRoute)
let atStop = false;       // le bus est à l'arrêt courant (embarquement en cours)
// currentSpeedKmh et lastFix vivaient ici sans jamais y être affectés, alors qu'app.js les
// utilisait : le piège était que le bundle non strict en faisait des globales implicites.
// Ils sont maintenant déclarés dans app.js, qui est le seul à s'en servir ; la vitesse
// arrive ici par l'accesseur getSpeedKmh() injecté.
let offRouteCount = 0;
let lastRecalcTs = 0;
let isRecalculating = false;
// Un recalcul part sans être attendu : il peut donc atterrir APRÈS un retour au menu ou le
// chargement d'un autre trajet. Sans ce jeton, sa bascule atomique réinstallerait les arrêts
// de la tournée précédente par-dessus la nouvelle.
let navGeneration = 0;

const MANEUVER_REACHED_M = 20;   // rayon de validation d'une manœuvre
// Longueur de tracé sur laquelle on mesure le cap d'arrivée à un arrêt. Le segment
// immédiatement précédent ne suffit pas : la densité des sommets d'OSRM varie, et un
// segment de deux mètres donne un cap qui ne veut rien dire.
const BEARING_SPAN_M = 25;
// Seuil d'ANNONCE VOCALE de l'arrêt, et non d'affichage : la pancarte n'apparaît qu'une
// fois le bus à l'arrêt. Le nom STOP_SIGN_M mentait sur ce rôle, d'où le renommage.
const STOP_ANNOUNCE_M = 100;
const STOP_HERE_SAY_M = 10;      // « Arrêt » ne se prononce qu'une fois le bus vraiment dessus
const STOP_STILL_KMH = 3;        // filet : bus immobile à l'arrêt, si aucun fix ne passe sous 10 m
const STOP_REACHED_M = 25;       // rayon dans lequel on considère être À l'arrêt
const STOP_LEFT_M = 40;          // il faut s'être éloigné d'autant pour passer au suivant
const OFF_ROUTE_M = 60;          // écart au tracé considéré comme « hors trajet »
const BACK_ON_ROUTE_M = 40;      // hystérésis, pour ne pas osciller autour du seuil
const OFF_ROUTE_FIXES = 3;       // fixes GPS consécutifs avant de recalculer
const RECALC_THROTTLE_MS = 15000;

function resetNavState() {
    navGeneration++;
    plannedBearings = [];
    navManeuvers = [];
    navStopPoints = [];
    navRouteCoords = [];
    navIndex = 0;
    navStopIndex = 0;
    navNearestIdx = 0;
    atStop = false;
    offRouteCount = 0;
    resetAnnouncements();
    lastRecalcTs = 0;
    isRecalculating = false;
}

// AbortSignal.timeout est récent : sur un navigateur qui ne l'a pas, on part sans
// délai d'expiration plutôt que de planter.


// Décodeur de polyligne à précision 6 : le format des tracés Valhalla.

// Valhalla numérote ses manœuvres. On les ramène au couple type/modifier d'OSRM pour
// que describeManeuver() et les icônes fonctionnent à l'identique, quel que soit le
// moteur qui a répondu.

// Reconstitue la forme d'une route OSRM (geometry + legs[].steps[]) à partir d'un trip
// Valhalla. Piège : chaque leg a SON propre tracé, et begin_shape_index est relatif à
// ce tracé-là, pas au trajet complet.


// Essaie chaque moteur dans l'ordre et renvoie la première route obtenue.

// Découpage en lots de 20 avec 1 point de CHEVAUCHEMENT : le dernier arrêt d'un lot est le
// premier du suivant, sinon le segment entre les deux lots serait absent du tracé.

function showNavError(msg) {
    view.error(msg);
    log("Erreur", msg);
}

// Récupère le trajet complet et construit la table des manœuvres. Remplace l'ancienne
// drawPlannedRoute() : alimente la même polyligne, plus navManeuvers / navStopPoints.
// Construit dans des variables LOCALES et ne remplace l'état global qu'en cas de succès.
// Auparavant resetNavState() était appelé avant la requête : un recalcul hors trajet qui
// échouait — c'est-à-dire tout recalcul sans réseau — effaçait la table des manœuvres et
// laissait le chauffeur sans guidage en pleine tournée.
async function buildNavigationRoute(waypoints, options) {
    options = options || {};
    if (!waypoints || waypoints.length < 2) return false;

    const maneuvers = [];
    const stopPoints = [];
    const routeCoords = [];

    isRecalculating = true;
    const generation = navGeneration;

    const chunks = chunkWaypoints(waypoints, OSRM_MAX_WAYPOINTS);
    // Les caps se découpent EXACTEMENT comme les waypoints — même pas, même chevauchement
    // d'un point — sinon une contrainte se retrouverait posée sur le mauvais arrêt.
    const bearingChunks = options.bearings ? chunkWaypoints(options.bearings, OSRM_MAX_WAYPOINTS) : null;
    let legOffset = 0;   // n° de leg global, pour retrouver l'arrêt correspondant

    try {
        for (let c = 0; c < chunks.length; c++) {
            const chunk = chunks[c];
            const route = await fetchRouteChunk(chunk, bearingChunks ? bearingChunks[c] : null);

            route.geometry.coordinates.forEach(pt => routeCoords.push([pt[1], pt[0]]));

            route.legs.forEach((leg, li) => {
                const globalLeg = legOffset + li;
                const lastIdx = leg.steps.length - 1;
                leg.steps.forEach((step, si) => {
                    const type = step.maneuver.type;
                    // Un 'depart' ailleurs qu'au tout début est un artefact du découpage
                    // en lots : on ne garde que celui du premier leg.
                    if (type === 'depart' && globalLeg > 0) return;
                    const isStop = (type === 'arrive') || si === lastIdx;
                    const wp = waypoints[globalLeg + 1];
                    const label = (wp && wp.label) || `Arrêt ${globalLeg + 1}`;
                    const info = describeManeuver(step);
                    maneuvers.push({
                        lat: step.maneuver.location[1],
                        lng: step.maneuver.location[0],
                        key: isStop ? 'arrive' : info.key,
                        action: isStop ? label : info.action,
                        name: info.name,
                        text: info.text,
                        isStop: isStop,
                        label: isStop ? label : null
                    });
                    if (isStop) {
                        stopPoints.push({
                            lat: step.maneuver.location[1],
                            lng: step.maneuver.location[0],
                            label: label,
                            maneuverIndex: maneuvers.length - 1
                        });
                    }
                });
            });
            legOffset += route.legs.length;

            // Pause de politesse : le serveur public OSRM limite le débit par IP.
            if (c < chunks.length - 1) await new Promise(r => setTimeout(r, 300));
        }
    } catch (err) {
        console.error(err);
        isRecalculating = false;
        // L'état précédent est intact. Si un guidage était déjà en cours, il continue sur
        // l'ancienne table plutôt que de disparaître.
        if (navManeuvers.length) {
            log("Routage", `Recalcul impossible : ${err.message}`);
            view.status('Recalcul impossible — guidage conservé');
        } else {
            showNavError(`Trajet non calculable : ${err.message}`);
        }
        return false;
    }

    // L'état a été réinitialisé pendant la requête (retour au menu, autre trajet chargé) :
    // ce résultat est périmé, on le jette au lieu d'écraser la tournée en cours.
    if (generation !== navGeneration) {
        isRecalculating = false;
        log("Navigation", "Recalcul arrivé trop tard — ignoré.");
        return false;
    }

    // Bascule atomique : rien n'a été touché avant ce point.
    // lastRecalcTs est délibérément préservé, sinon la limitation à un recalcul par
    // 15 s serait remise à zéro à chaque recalcul réussi.
    plannedWaypoints = waypoints;
    navManeuvers = maneuvers;
    navStopPoints = stopPoints;
    navRouteCoords = routeCoords;
    navIndex = 0;
    navStopIndex = 0;
    navNearestIdx = 0;
    atStop = false;
    offRouteCount = 0;
    resetAnnouncements();

    // Le sens de référence est celui du PREMIER plan, et il ne bouge plus : le recalculer
    // sur le tracé d'un recalcul ferait d'un passage à contresens la nouvelle vérité.
    if (!plannedBearings.length) plannedBearings = measureStopBearings(routeCoords, stopPoints);

    view.route(navRouteCoords, !options.keepView);
    isRecalculating = false;
    log("Navigation", `${navStopPoints.length} arrêt(s), ${navManeuvers.length} manœuvre(s) chargée(s).`);
    return true;
}

// Cap d'arrivée à chaque arrêt, mesuré sur le tracé. Le résultat est aligné sur
// plannedWaypoints, dont l'index 0 est le DÉPART : d'où le null de tête. Les arrêts sont
// dans l'ordre du tracé, un seul parcours avec un curseur qui avance suffit donc.
function measureStopBearings(routeCoords, stopPoints) {
    const out = [null];
    if (routeCoords.length < 2) {
        stopPoints.forEach(() => out.push(null));
        return out;
    }
    let cursor = 0;
    for (const stop of stopPoints) {
        let best = cursor, bestD = Infinity;
        for (let i = cursor; i < routeCoords.length; i++) {
            const d = haversineMeters(stop.lat, stop.lng, routeCoords[i][0], routeCoords[i][1]);
            if (d < bestD) { bestD = d; best = i; }
        }
        // On remonte BEARING_SPAN_M en arrière pour obtenir un cap d'approche stable.
        let from = best;
        let span = 0;
        while (from > 0 && span < BEARING_SPAN_M) {
            span += haversineMeters(routeCoords[from][0], routeCoords[from][1],
                                    routeCoords[from - 1][0], routeCoords[from - 1][1]);
            from--;
        }
        const to = Math.max(best, from + 1);
        out.push(from === to ? null
                 : bearingDegrees(routeCoords[from][0], routeCoords[from][1],
                                  routeCoords[to][0], routeCoords[to][1]));
        cursor = best;
    }
    return out;
}


// Appelée à chaque fix GPS. Aucune requête réseau ici : tout est local.
function updateGuidance(lat, lng) {
    // 1. Avancer dans les manœuvres
    while (navIndex < navManeuvers.length) {
        const m = navManeuvers[navIndex];
        const d = haversineMeters(lat, lng, m.lat, m.lng);
        if (d < MANEUVER_REACHED_M) { navIndex++; continue; }
        // Filet si un fix GPS a été sauté : la manœuvre suivante est déjà nettement plus
        // proche que la courante, donc on l'a dépassée.
        const next = navManeuvers[navIndex + 1];
        if (next && haversineMeters(lat, lng, next.lat, next.lng) < d - MANEUVER_REACHED_M) {
            navIndex++; continue;
        }
        break;
    }

    // 2. Arrêt courant. Le passage au suivant se fait normalement par confirmStop() — le
    //    chauffeur tape la pancarte quand tout le monde est monté. L'éloignement de 40 m
    //    n'est qu'un FILET : sans lui, un oubli figerait la pancarte à l'écran et bloquerait
    //    les annonces de tous les arrêts suivants pour le reste de la tournée.
    const cur = navStopPoints[navStopIndex];
    if (cur) {
        const dCur = haversineMeters(lat, lng, cur.lat, cur.lng);
        if (dCur < STOP_REACHED_M) {
            if (!atStop) {
                atStop = true;
                log("Arrêt", `${cur.label} atteint.`);
            }
            // « Arrêt dans 100 mètres » a déjà prévenu ; ici on ne fait que confirmer
            // l'arrivée, et pas à 25 m — à cette distance le bus roule encore. Le filet
            // couvre le fix imprécis qui ne descend jamais sous 10 m : un bus immobile sur
            // l'arrêt y est.
            if (dCur <= STOP_HERE_SAY_M || getSpeedKmh() < STOP_STILL_KMH) {
                announceOnce('ici' + navStopIndex, 0, 1, 'Arrêt');
            }
        } else if (atStop && dCur > STOP_LEFT_M) {
            atStop = false;
            navStopIndex++;
            log("Arrêt", `${cur.label} quitté sans confirmation — passage au suivant.`);
        }
    }

    // 3. Fin de trajet
    if (navIndex >= navManeuvers.length && navStopIndex >= navStopPoints.length) {
        view.finished();
        announceOnce('fin', 0, 1, 'Trajet terminé.');
        return;
    }

    // 4. Le bandeau montre TOUJOURS la prochaine manœuvre — les arrivées en sont, donc un
    //    arrêt qui approche s'y affiche comme le reste. Auparavant la pancarte le remplaçait
    //    dès 150 m et masquait l'instruction pendant tout ce temps.
    const dTurn = renderTurn(lat, lng);
    const mv = navManeuvers[navIndex];
    // Un arrêt est AUSSI une manœuvre : le laisser parler doublerait « Arrêt dans 100
    // mètres » d'un « Arrivée à l'arrêt » qui la coupe. Le bandeau, lui, continue de
    // l'afficher. Et pendant l'embarquement on se tait : la manœuvre suivante devient
    // courante dès 20 m, son annonce attendra le départ.
    if (mv && !mv.isStop && !atStop && Number.isFinite(dTurn)) {
        announce('m' + navIndex, dTurn, `Dans ${spokenDistance(dTurn)}, ${mv.text}`, mv.text);
    }

    // 5. Le prochain arrêt. La ligne du bandeau est mise à jour à TOUTES les positions,
    //    embarquement compris : une distance qui apparaît et disparaît sous les yeux du
    //    chauffeur se cherche, une ligne qui ne bouge jamais de place se lit d'un coup d'œil.
    const stop = navStopPoints[navStopIndex];
    if (stop) {
        const dStop = haversineMeters(lat, lng, stop.lat, stop.lng);
        view.stopAhead({ label: stop.label, distance: formatDistance(dStop) });
        // On prononce le PALIER et non la distance mesurée : selon le fix qui franchit le
        // seuil, spokenDistance(dStop) donnerait « Arrêt dans 90 mètres ».
        if (!atStop) {
            announceOnce('s' + navStopIndex, dStop, STOP_ANNOUNCE_M,
                         `Arrêt dans ${spokenDistance(STOP_ANNOUNCE_M)}`);
        }
    }

    // La pancarte, elle, n'apparaît qu'une fois le bus À l'arrêt. Elle s'ajoute au bandeau
    // au lieu de le remplacer.
    if (atStop) renderStopSign(stop ? stop.label : '');
    else view.hideStop();

    // 6. Hors trajet
    checkOffRoute(lat, lng);
}

// Renvoie la distance à la manœuvre visée, pour que l'appelant puisse l'annoncer.
function renderTurn(lat, lng) {
    const m = navManeuvers[navIndex];
    if (!m) return NaN;
    const d = haversineMeters(lat, lng, m.lat, m.lng);
    // action et street séparés : le bandeau affiche l'action en gros et la rue en dessous.
    // Sans nom de rue (OSRM n'en a pas toujours), la ligne de rue reste vide plutôt que
    // d'être remplie du remplissage « la route sans nom ».
    view.turn({ iconKey: m.key, distance: formatDistance(d), action: m.action, street: m.name || '' });
    return d;
}

// La pancarte n'existant plus qu'à l'arrêt, il n'y a plus de distance à décompter dessus.
function renderStopSign(label) {
    view.stop({ here: true, distance: 'EMBARQUEMENT', label: label || '' });
}

// Confirmation manuelle de l'embarquement : le chauffeur tape la pancarte quand tout le monde
// est monté. C'est le geste normal ; le filet des 40 m du bloc 2 ne rattrape que les oublis.
// Renvoie false si on n'est pas à un arrêt, pour que l'interface ne fasse rien.
function confirmStop() {
    if (!atStop) return false;
    const cur = navStopPoints[navStopIndex];
    atStop = false;
    navStopIndex++;
    log("Arrêt", `${cur ? cur.label : 'Arrêt'} confirmé — au suivant.`);
    return true;
}

// Renonce à l'arrêt courant : le bus ne s'y arrêtera pas. À la différence de confirmStop(),
// n'exige PAS d'être sur place — c'est tout l'objet du bouton. Recalcule dans la foulée,
// sans attendre la limitation des 15 s : le tracé planifié repasse encore par l'arrêt
// abandonné, le laisser en place enverrait le chauffeur faire demi-tour pour rien.
function skipStop(lat, lng) {
    const cur = navStopPoints[navStopIndex];
    if (!cur) return false;
    atStop = false;
    navStopIndex++;
    log("Arrêt", `${cur.label} sauté — non desservi.`);
    if (Number.isFinite(lat) && Number.isFinite(lng)) recalculateFrom(lat, lng);
    return true;
}


// Recalcule depuis la position actuelle vers l'arrêt COURANT (non encore desservi) et les
// suivants. Le cap relevé sur le plan d'origine est transmis au routage : sans lui, un
// recalcul peut ramener le bus sur l'arrêt à contresens, porte du mauvais côté de la rue.
function recalculateFrom(lat, lng) {
    // plannedWaypoints[0] est le départ, d'où le décalage : l'arrêt courant est conservé.
    const remaining = plannedWaypoints.slice(navStopIndex + 1);
    if (!remaining.length) return false;
    const candidate = [{ lat: lat, lng: lng, type: 'start', label: 'Position actuelle' }].concat(remaining);
    const bearings = [null].concat(plannedBearings.slice(navStopIndex + 1));
    lastRecalcTs = Date.now();
    // plannedWaypoints n'est PAS écrasé ici : buildNavigationRoute ne l'installe qu'en cas
    // de succès. Sinon un échec hors ligne détruirait la liste des arrêts restants.
    buildNavigationRoute(candidate, { keepView: true, bearings: bearings });
    return true;
}


function checkOffRoute(lat, lng) {
    if (isRecalculating || navRouteCoords.length < 2) return;
    // distanceToRoute est devenue pure : elle reçoit le tracé et le curseur, et renvoie
    // le curseur mis à jour.
    const probe = distanceToRoute(lat, lng, navRouteCoords, navNearestIdx);
    navNearestIdx = probe.idx;
    const d = probe.dist;

    if (d > OFF_ROUTE_M) {
        offRouteCount++;
    } else if (d < BACK_ON_ROUTE_M) {
        offRouteCount = 0;   // franchement revenu sur le tracé
        return;
    } else {
        return;              // zone tampon 40-60 m : on ne change rien
    }

    if (offRouteCount < OFF_ROUTE_FIXES) return;
    const now = Date.now();
    if (now - lastRecalcTs < RECALC_THROTTLE_MS) return;

    view.status('Hors trajet — recalcul…');
    log("Navigation", "Hors trajet : recalcul de l'itinéraire.");
    if (!recalculateFrom(lat, lng)) offRouteCount = 0;
}

export {
    initGuidance, resetNavState, buildNavigationRoute, updateGuidance, checkOffRoute,
    showNavError, confirmStop, skipStop, recalculateFrom,
    MANEUVER_REACHED_M, STOP_ANNOUNCE_M, STOP_HERE_SAY_M, STOP_REACHED_M, STOP_LEFT_M,
    OFF_ROUTE_M, BACK_ON_ROUTE_M, OFF_ROUTE_FIXES, RECALC_THROTTLE_MS
};

// Accès en lecture à l'état, pour l'interface et les tests. Les liaisons ESM d'un « let »
// sont en lecture seule à l'import, d'où ces accesseurs.
export const nav = {
    get maneuvers() { return navManeuvers; },
    get stopPoints() { return navStopPoints; },
    get index() { return navIndex; },
    get stopIndex() { return navStopIndex; },
    get routeCoords() { return navRouteCoords; },
    get atStop() { return atStop; },
    get waypoints() { return plannedWaypoints; },
    get bearings() { return plannedBearings; },
    get isRecalculating() { return isRecalculating; },
    get offRouteCount() { return offRouteCount; },
    set lastRecalcTs(v) { lastRecalcTs = v; },
    get lastRecalcTs() { return lastRecalcTs; },
    set waypoints(v) { plannedWaypoints = v; }
};
